import { GenericActionWithPayload } from "./action-types";
import { DeleteBasketPayloadType, EditBasketPayloadType } from "./basket";
import {
  AddToBasketPayloadType,
  ClearBasketAction,
  ClearBasketAfterOrderAction,
} from "./line-items";
import { AllergensType, Product } from "./product";

type OptionsType = {
  choices: OptionChoice[];
  id: number;
  cost: number;
  multipleChoices: boolean;
  required: boolean;
  name: string;
  nutritionalInformation: OptionChoice[];
};

type OptionChoice = {
  additive: boolean;
  calories: number;
  default: boolean;
  id: number;
  imageUrl: string;
  name: string;
  price: number;
  selected?: boolean;
  allergenInformation: AllergensType;
};

interface Choices {
  optionId: number;
  choiceId: number;
}

interface ChoicesWithLine {
  lineId: number;
}

interface SelectedChoice {
  choiceId: number;
  name: string;
  price?: number;
}

interface SelectedOption {
  optionId: number;
  name: string;
}

interface SelectedOptions {
  option: SelectedOption;
  choices?: SelectedChoice;
  price?: number;
}

interface SelectedLineOptions {
  lineId: number;
  quantity?: number;
  options: SelectedOptions[];
}

interface OrderLineChoice {
  optionId: number;
  choiceId: number;
}

interface ComboChoiceForOrder {
  productId: number;
  quantity: number;
  isUpselling: boolean;
}

interface OrderLinesWithOptions {
  productId: number;
  quantity: number;
  choices?: OrderLineChoice[];
  comboChoices?: ComboChoiceForOrder[];
}

type ChoicesAndLine = Choices & ChoicesWithLine;

interface LineOptions {
  [key: number]: OptionsType[] | undefined;
}

type ComboSectionType = {
  id: number;
  name: string;
  requiredSelections: number;
  allowDuplicateSelections: boolean;
  isUpselling: boolean;
  products: Omit<Product, "section">[];
};

interface CopyActionPayload {
  lineId: number;
  options: OptionsType[];
}

type AddToBasketAsyncAction = GenericActionWithPayload<
  "ADD_TO_BASKET_ASYNC",
  AddToBasketPayloadType
>;

type UpdateAllOptionsAsyncAction = GenericActionWithPayload<
  "UPDATE_ALL_OPTIONS_ASYNC",
  EditBasketPayloadType
>;

type DeleteBasketAsyncAction = GenericActionWithPayload<
  "DELETE_BASKET_ASYNC",
  DeleteBasketPayloadType
>;

type ModifyOptionsAsyncAction = GenericActionWithPayload<
  "MODIFY_OPTIONS_ASYNC",
  ChoicesAndLine
>;

type ModifyOptionsAction = GenericActionWithPayload<
  "MODIFY_OPTIONS",
  ChoicesAndLine
>;

type CopyOptionsAction = GenericActionWithPayload<
  "COPY_OPTIONS",
  CopyActionPayload
>;

type CopyLineOptionsAction = GenericActionWithPayload<
  "COPY_LINE_OPTIONS",
  CopyActionPayload
>;

type UpdateOptionForLineAction = GenericActionWithPayload<
  "UPDATE_OPTION_FOR_LINE",
  ChoicesWithLine
>;

type EditIndividualOptionsAction = GenericActionWithPayload<
  "EDIT_INDIVIDUAL_OPTIONS",
  number
>;

type UpdateIndividualOptionsAsyncAction = GenericActionWithPayload<
  "UPDATE_INDIVIDUAL_OPTIONS_ASYNC",
  number
>;

type DeleteIndividualOptionsAction = GenericActionWithPayload<
  "DELETE_INDIVIDUAL_OPTIONS",
  number
>;

type DeleteLineOptionsAsyncAction = {
  type: "DELETE_LINE_OPTIONS_ASYNC";
  lineItemId: string;
};

type DeleteOptionLineAsyncAction = {
  type: "DELETE_OPTION_LINE_ASYNC";
  lineId: string;
};

type CleanupAsyncAction = {
  type: "CLEANUP_ASYNC";
  lineItemId: string;
};

type RevertChildLinesAsyncAction = {
  type: "REVERT_CHILD_LINES_ASYNC";
  lineItemId: string;
};

type DeleteOptionLineAction = GenericActionWithPayload<
  "DELETE_OPTION_LINE",
  number
>;

type DeleteSavedLineOptionsAction = GenericActionWithPayload<
  "DELETE_SAVED_LINE_OPTIONS",
  ChoicesWithLine
>;

type OptionActions =
  | UpdateAllOptionsAsyncAction
  | DeleteBasketAsyncAction
  | ModifyOptionsAsyncAction
  | CopyOptionsAction
  | CopyLineOptionsAction
  | UpdateOptionForLineAction
  | ModifyOptionsAction
  | DeleteIndividualOptionsAction
  | DeleteLineOptionsAsyncAction
  | DeleteSavedLineOptionsAction
  | DeleteOptionLineAsyncAction
  | AddToBasketAsyncAction
  | ClearBasketAction
  | ClearBasketAfterOrderAction
  | UpdateIndividualOptionsAsyncAction;

export type {
  Choices,
  ChoicesWithLine,
  ChoicesAndLine,
  LineOptions,
  CopyActionPayload,
  CopyLineOptionsAction,
  SelectedOptions,
  SelectedLineOptions,
  OrderLineChoice,
  ComboChoiceForOrder,
  OrderLinesWithOptions,
  OptionsType,
  OptionChoice,
  ComboSectionType,
  UpdateAllOptionsAsyncAction,
  DeleteBasketAsyncAction,
  ModifyOptionsAsyncAction,
  CopyOptionsAction,
  UpdateOptionForLineAction,
  OptionActions,
  EditIndividualOptionsAction,
  DeleteIndividualOptionsAction,
  DeleteLineOptionsAsyncAction,
  DeleteOptionLineAsyncAction,
  DeleteSavedLineOptionsAction,
  DeleteOptionLineAction,
  AddToBasketAsyncAction,
  CleanupAsyncAction,
  RevertChildLinesAsyncAction,
  UpdateIndividualOptionsAsyncAction,
};
